import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Calendar as ReactCalendar,
  momentLocalizer,
  Views,
  move,
} from 'react-schedule-calendar';
import { navigate } from 'react-schedule-calendar/lib/utils/constants';
import { mergeWithDefaults } from 'react-schedule-calendar/lib/localizer';
import moment from 'moment';
import { Swipeable } from 'react-swipeable';
import withStyles from '@mui/styles/withStyles';
import 'react-schedule-calendar/lib/css/react-big-calendar.css';
import './Calendar.css';

import { Divider, ListItemText, ListItemIcon } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import SubjectIcon from '@mui/icons-material/Description';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ListItemButton from '@mui/material/ListItemButton';
import Toolbar from '../Toolbar';
import Event from './Event';
import AirportsFilter from '../AirportsFilter';
import Drawer from '../Drawer';
import CalendarViews from './CalendarViews';
import QuickDayView from './QuickDayView';
import StaffAllocationView from './StaffAllocation/StaffAllocationView';
import WeekView from './WeekView';
// import UserBadge from '../UserBadge';
import Spacer from '../Spacer';
import LanguagesList from '../LanguagesList';

const VIEW_KEY = 'currentView';
const CULTURE = 'en';
moment.updateLocale(CULTURE, {
  week: {
    dow: 1,
  },
});
const localizer = momentLocalizer(moment);

const styles = theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 0%',
    overflow: 'hidden',
  },
  content: {
    flex: '1 0 0%',
    display: 'flex',
    overflow: 'hidden',
  },
  toolbar: {
    flex: '1 0 0%',
  },
  menuButton: {
    marginLeft: 12,
    marginRight: 20,
  },
  calendar: {
    flex: '1 0 0%',
    overflow: 'hidden',
  },
  event: {
    [theme.breakpoints.down('sm')]: {
      maxWidth: '20px',
    },
  },
  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
});

class Calendar extends Component {
  constructor(props) {
    super(props);

    const date = new Date();
    this.state = {
      isDrawerOpen: false,
      view: localStorage.getItem(VIEW_KEY) || Views.MONTH,
      views: {
        [Views.MONTH]: true,
        // FIXME: What's the native week view?
        [Views.WEEK]: WeekView,
        // [Views.DAY]: true,
        [Views.AGENDA]: true,
        quickDay: QuickDayView,
        'staff allocation': StaffAllocationView,
      },
      date,
      culture: CULTURE,
      localizer: mergeWithDefaults(localizer, CULTURE),
    };

    this.calendar = React.createRef();
  }

  componentDidMount() {
    const { onRangeChange } = this.props;
    const { date } = this.state;
    if (onRangeChange) onRangeChange(this.getViewRange(date));
  }

  toggleDrawer = () => {
    const { isDrawerOpen } = this.state;
    this.setState({ isDrawerOpen: !isDrawerOpen });
  };

  handleDrawerOpen = () => {
    this.toggleDrawer();
  };

  handleDrawerClose = () => {
    this.toggleDrawer();
  };

  handleViewChange = view => {
    const { onRangeChange } = this.props;
    const { date } = this.state;
    this.setState(
      { view },
      () => onRangeChange && onRangeChange(this.getViewRange(date))
    );
    localStorage.setItem(VIEW_KEY, view);
  };

  handleNavigate = action => {
    const { onRangeChange } = this.props;
    const { view, date } = this.state;

    let newDate = null;

    if (view === 'quickDay') {
      newDate = move(QuickDayView, {
        action,
        date,
      });
    } else if (view === 'staff allocation') {
      newDate = move(StaffAllocationView, {
        action,
        date,
      });
    } else {
      newDate = move(view, {
        action,
        date,
      });
    }

    if (onRangeChange) onRangeChange(this.getViewRange(newDate));
    this.setState({ date: newDate });
  };

  handleFilterChange = (name, selected) => {
    const { onAirportFilterChange } = this.props;
    onAirportFilterChange(name, selected);
  };

  handleFilterClear = () => {
    const { onAirportFilterClear } = this.props;
    onAirportFilterClear();
  };

  handleSelectEvent = async event => {
    const { onEventSelect } = this.props;
    onEventSelect(event._id);
  };

  handleCalendarSwipeLeft = () => {
    this.handleNavigate(navigate.NEXT);
  };

  handleCalendarSwipeRight = () => {
    this.handleNavigate(navigate.PREVIOUS);
  };

  handleCalendarViewChange = view => {
    this.setState({ view });
  };

  handleCalendarNavigate = date => {
    this.setState({ date });
  };

  handleCalendarRangeChange = range => {
    const { onRangeChange } = this.props;
    if (onRangeChange) onRangeChange(range);
  };

  getViewRange = date => {
    return this.calendar.current.getView().range(date, { localizer });
  };

  render() {
    const { classes, events, airports, onLogout, t } = this.props;
    const { isDrawerOpen, view, views, date, localizer, culture } = this.state;
    const calendar = this.calendar.current;
    const label = calendar ? calendar.getView().title(date, { localizer }) : '';

    return (
      <Swipeable
        className={classes.container}
        onSwipedLeft={this.handleCalendarSwipeLeft}
        onSwipedRight={this.handleCalendarSwipeRight}
      >
        <Toolbar
          className={classes.toolbar}
          label={label}
          view={view}
          onViewChange={this.handleViewChange}
          onDrawerOpen={this.handleDrawerOpen}
          onNavigate={this.handleNavigate}
        />
        <div className={classes.content}>
          <Drawer isOpen={isDrawerOpen} onClose={this.handleDrawerClose}>
            <div className={classes.drawerContent}>
              {/*TODO implement UserBadge*/}
              {/*<UserBadge fullName="Roman Kutepov" status="Admin" />*/}
              <CalendarViews
                view={view}
                views={views}
                onChange={this.handleViewChange}
              />
              <Divider />
              <AirportsFilter
                airports={airports}
                onFilterChange={this.handleFilterChange}
                onFilterClear={this.handleFilterClear}
              />
              <Divider />
              <ListItemButton component={Link} to="/reports">
                <ListItemIcon>
                  <SubjectIcon />
                </ListItemIcon>
                <ListItemText>{t('Reports')}</ListItemText>
              </ListItemButton>
              <ListItemButton component={Link} to="/statistics">
                <ListItemIcon>
                  <BarChartIcon />
                </ListItemIcon>
                <ListItemText>{t('Statistics')}</ListItemText>
              </ListItemButton>
              <ListItemButton component={Link} to="/users">
                <ListItemIcon>
                  <PeopleAltOutlinedIcon />
                </ListItemIcon>
                <ListItemText>{t('Users')}</ListItemText>
              </ListItemButton>
              <Spacer />
              <LanguagesList />
              <Divider />
              <ListItemButton onClick={onLogout}>
                <ListItemIcon>
                  <MeetingRoomIcon />
                </ListItemIcon>
                <ListItemText>{t('Logout')}</ListItemText>
              </ListItemButton>
            </div>
          </Drawer>
          <ReactCalendar
            ref={this.calendar}
            className={classes.calendar}
            events={events}
            localizer={localizer}
            culture={culture}
            defaultDate={new Date()}
            view={view}
            views={views}
            date={date}
            toolbar={false}
            components={{ event: Event }}
            drilldownView={'quickDay'}
            onView={this.handleCalendarViewChange}
            onNavigate={this.handleCalendarNavigate}
            onRangeChange={this.handleCalendarRangeChange}
            onSelectEvent={this.handleSelectEvent}
          />
        </div>
      </Swipeable>
    );
  }
}

export default withTranslation()(withStyles(styles)(Calendar));
